import type {
  ProvidersServiceAPI,
  DataBindingServiceAPI,
} from '@wix/editor-platform-feature-store';
import type { Breakpoint, ContainerLayoutTransitionConfig } from '../types';
import type { LAYOUT, Presets } from '@wix/component-protocol/schema';
import type {
  CompRef,
  Link,
  PopulatedComponentContainerInfoMap,
  TpaStyle,
  TpaStyleParamValue,
  ValueOf,
} from '@wix/document-services-types';
import type { ManifestComponentExtensionType } from '../stores';

import type { EditorPointer } from '@wix/editor-document-logic-api';

export enum DependencyToken {
  // Sets component data for builder (EditorReactComponent) components
  BUILDER_DATA_UPDATE = 'BUILDER_DATA_UPDATE',
  // Retrieves component data for builder components
  BUILDER_DATA_GET = 'BUILDER_DATA_GET',

  UNKNOWN_COMPONENT_DATA_GET = 'UNKNOWN_COMPONENT_DATA_GET',
  UNKNOWN_COMPONENT_DATA_UPDATE = 'UNKNOWN_COMPONENT_DATA_UPDATE',

  // Retrieves TPA data by key via callback for OOI widgets
  OOI_DATA_GET = 'OOI_DATA_GET',
  // Sets multiple TPA data entries for OOI widgets
  OOI_DATA_UPDATE = 'OOI_DATA_UPDATE',

  // Styles

  // Middleware that wraps setStyles calls,
  // allowing consumers to intercept and extend style updates
  STYLES_UPDATE_ROUTER = 'STYLES_UPDATE_ROUTER',

  // Retrieves CSS styles (properties and custom properties) for builder components
  BUILDER_STYLES_GET = 'BUILDER_STYLES_GET',
  // Updates CSS styles for builder components via host API
  BUILDER_STYLES_UPDATE = 'BUILDER_STYLES_UPDATE',
  // Removes CSS style properties by path for builder components
  BUILDER_STYLES_REMOVE = 'BUILDER_STYLES_REMOVE',

  // Retrieves CSS styles for components with unknown extension type
  UNKNOWN_COMPONENT_STYLES_GET = 'UNKNOWN_COMPONENT_STYLES_GET',
  // Updates CSS styles for components with unknown extension type
  UNKNOWN_COMPONENT_STYLES_UPDATE = 'UNKNOWN_COMPONENT_STYLES_UPDATE',
  // Removes CSS style properties for components with unknown extension type
  UNKNOWN_COMPONENT_STYLES_REMOVE = 'UNKNOWN_COMPONENT_STYLES_REMOVE',

  // Retrieves effective (merged/computed) TPA styles for OOI widgets
  OOI_STYLE_GET_EFFECTIVE = 'OOI_STYLE_GET_EFFECTIVE',
  // Retrieves non-effective (scoped) TPA styles for OOI widgets
  OOI_STYLE_GET = 'OOI_STYLE_GET',
  // Sets TPA style param values without triggering side effects
  OOI_STYLE_UPDATE = 'OOI_STYLE_UPDATE',

  // Stage

  // Gets the current responsive breakpoint from an editor pointer
  STAGE_BREAKPOINT_GET = 'STAGE_BREAKPOINT_GET',
  // Creates an editor pointer with a specific breakpoint variant applied
  STAGE_BREAKPOINT_POINTER_GET = 'STAGE_BREAKPOINT_POINTER_GET',

  // Props

  // Removes runtime component props by path via DM
  PROPS_REMOVE = 'PROPS_REMOVE',
  // Updates runtime component props by path via DM
  PROPS_UPDATE = 'PROPS_UPDATE',
  // Retrieves runtime component props by path via DM
  PROPS_GET = 'PROPS_GET',
  PROPS_OVERRIDES_UPDATE = 'PROPS_OVERRIDES_UPDATE',
  PROPS_OVERRIDES_GET = 'PROPS_OVERRIDES_GET',
  PROPS_OVERRIDES_REMOVE = 'PROPS_OVERRIDES_REMOVE',
  // Waits for all pending Document Manager changes to be applied
  DM_CHANGES_WAIT = 'DM_CHANGES_WAIT',

  // Layout

  // Applies component layout resize via editor host API
  LAYOUT_COMPONENT_UPDATE = 'LAYOUT_COMPONENT_UPDATE',
  LAYOUT_CONTAINER_REGENERATE = 'LAYOUT_CONTAINER_REGENERATE',

  // State

  // Gets the current view state of an OOI widget
  VIEW_STATE_GET = 'VIEW_STATE_GET',
  // Sets the view state of an OOI widget
  VIEW_STATE_UPDATE = 'VIEW_STATE_UPDATE',
  // Retrieves stage data (including view states) from DM platform controllers
  DM_STAGE_DATA_GET = 'DM_STAGE_DATA_GET',

  // Design States
  DESIGN_STATES_GET = 'DESIGN_STATES_GET',
  DESIGN_STATES_SET = 'DESIGN_STATES_SET',
  DESIGN_STATES_RESET = 'DESIGN_STATES_RESET',

  // Presets

  // Gets the effective (merged/inherited) preset for a builder component
  BUILDER_PRESETS_GET_EFFECTIVE = 'BUILDER_PRESETS_GET_EFFECTIVE',
  // Gets the scoped (non-inherited) preset for a builder component
  BUILDER_PRESETS_GET = 'BUILDER_PRESETS_GET',
  // Applies a preset by name to a builder component
  BUILDER_PRESETS_UPDATE = 'BUILDER_PRESETS_UPDATE',

  // Resolved Data

  // Retrieves cached SVG metadata for a vector image URI
  DM_SVG_INFO_GET = 'DM_SVG_INFO_GET',
  // Resolves the component type from client spec map by editor pointer
  COMP_TYPE_GET = 'COMP_TYPE_GET',
  // Resolves a human-readable display name for a link object
  LINK_DISPLAY_NAME_RESOLVE = 'LINK_DISPLAY_NAME_RESOLVE',

  // Components

  // Gets child component references from Document Manager
  COMPONENTS_CHILDREN_GET = 'COMPONENTS_CHILDREN_GET',
  // Gets the parent container of a component from Document Manager
  COMPONENTS_CONTAINER_GET = 'COMPONENTS_CONTAINER_GET',
  // Gets slot names for a component from Document Manager
  COMPONENTS_SLOTS_GET_NAMES = 'COMPONENTS_SLOTS_GET_NAMES',
  // Gets slots data (slot name to CompRef mapping) from Document Manager
  COMPONENTS_SLOTS_GET_DATA = 'COMPONENTS_SLOTS_GET_DATA',
  /** Gets populated builder-specific slot data via the Builder Slots API */
  COMPONENTS_SLOTS_BUILDER_GET_DATA = 'COMPONENTS_SLOTS_BUILDER_GET_DATA',

  // Providers

  // Lists available providers with optional filtering
  PROVIDERS_LIST = 'PROVIDERS_LIST',
  // Attaches a provider to a component
  PROVIDERS_ATTACH = 'PROVIDERS_ATTACH',
  // Detaches a provider from a component
  PROVIDERS_DETACH = 'PROVIDERS_DETACH',
  // Gets provider data for a component
  PROVIDERS_GET_DATA = 'PROVIDERS_GET_DATA',
  // Updates provider data for a component
  PROVIDERS_UPDATE_DATA = 'PROVIDERS_UPDATE_DATA',

  // Bindings

  // Binds data to a component
  BINDINGS_BIND = 'BINDINGS_BIND',
  // Sets bindings on a component from a flat property-name -> binding map
  BINDINGS_SET_BINDING = 'BINDINGS_SET_BINDING',
  // Unbinds data from a component
  BINDINGS_UNBIND = 'BINDINGS_UNBIND',
  // Lists bindings for a component
  BINDINGS_LIST = 'BINDINGS_LIST',
  // Gets bindings for a component as a flat property-name -> binding map
  BINDINGS_GET_BINDING = 'BINDINGS_GET_BINDING',
  // Resolves binding candidates for a data type or function parameter
  BINDINGS_FIND_BINDING_CANDIDATES = 'BINDINGS_FIND_BINDING_CANDIDATES',

  // Panorama

  // Wraps a function call with panorama transaction tracking
  PANORAMA_TRACK_TRANSACTION = 'PANORAMA_TRACK_TRANSACTION',

  // Retrieves runtime UX data labels for a component and language
  UX_DATA_GET_WITH_DEFAULTS = 'UX_DATA_GET_WITH_DEFAULTS',
}

export interface IDependencyToken<TToken extends DependencyToken, TAPI> {
  type: TToken;
  api: TAPI;
}

export type IDependencyAPI =
  | IDependencyToken<
      DependencyToken.BUILDER_DATA_UPDATE,
      (
        editorPointer: EditorPointer<CompRef>,
        data: Record<string, unknown>,
      ) => void
    >
  | IDependencyToken<
      DependencyToken.BUILDER_DATA_GET,
      (
        editorPointer: EditorPointer<CompRef>,
      ) => Record<string, unknown> | undefined
    >
  | IDependencyToken<
      DependencyToken.UNKNOWN_COMPONENT_DATA_UPDATE,
      (
        editorPointer: EditorPointer<CompRef>,
        data: Record<string, unknown>,
      ) => void
    >
  | IDependencyToken<
      DependencyToken.UNKNOWN_COMPONENT_DATA_GET,
      (
        editorPointer: EditorPointer<CompRef>,
      ) => Record<string, unknown> | undefined
    >
  | IDependencyToken<
      DependencyToken.OOI_DATA_GET,
      (
        editorPointer: EditorPointer<CompRef>,
        key: string,
        cb: (value: Record<string, any>) => void,
      ) => void
    >
  | IDependencyToken<
      DependencyToken.OOI_DATA_UPDATE,
      (editorPointer: EditorPointer<CompRef>, data: Record<string, any>) => void
    >
  // Styles
  | IDependencyToken<
      DependencyToken.STYLES_UPDATE_ROUTER,
      (
        setStyles: (
          editorPointer: EditorPointer<CompRef>,
          type: ManifestComponentExtensionType,
          styles: {
            cssProperties: Record<string, string>;
            cssCustomProperties: Record<string, string>;
          },
        ) => void,
      ) => (
        editorPointer: EditorPointer<CompRef>,
        type: ManifestComponentExtensionType,
        styles: {
          cssProperties: Record<string, string>;
          cssCustomProperties: Record<string, string>;
        },
      ) => void
    >
  | IDependencyToken<
      DependencyToken.BUILDER_STYLES_GET,
      (
        editorPointer: EditorPointer<CompRef>,
        options?: {
          customGetDefaultStyle?: () => {
            cssProperties: Record<string, string>;
            cssCustomProperties: Record<string, string>;
          };
        },
      ) => {
        cssProperties?: Record<string, string>;
        cssCustomProperties?: Record<string, string>;
      }
    >
  | IDependencyToken<
      DependencyToken.BUILDER_STYLES_UPDATE,
      (
        editorPointer: EditorPointer<CompRef>,
        styles: {
          cssProperties?: Record<string, string>;
          cssCustomProperties?: Record<string, string>;
        },
      ) => void
    >
  | IDependencyToken<
      DependencyToken.BUILDER_STYLES_REMOVE,
      (editorPointer: EditorPointer<CompRef>, styles: string[]) => void
    >
  | IDependencyToken<
      DependencyToken.UNKNOWN_COMPONENT_STYLES_GET,
      (
        editorPointer: EditorPointer<CompRef>,
        options?: {
          customGetDefaultStyle?: () => {
            cssProperties: Record<string, string>;
            cssCustomProperties: Record<string, string>;
          };
        },
      ) => {
        cssProperties?: Record<string, string>;
        cssCustomProperties?: Record<string, string>;
      }
    >
  | IDependencyToken<
      DependencyToken.UNKNOWN_COMPONENT_STYLES_UPDATE,
      (
        editorPointer: EditorPointer<CompRef>,
        styles: {
          cssProperties?: Record<string, string>;
          cssCustomProperties?: Record<string, string>;
        },
      ) => void
    >
  | IDependencyToken<
      DependencyToken.UNKNOWN_COMPONENT_STYLES_REMOVE,
      (editorPointer: EditorPointer<CompRef>, styles: string[]) => void
    >
  | IDependencyToken<
      DependencyToken.OOI_STYLE_GET_EFFECTIVE,
      (editorPointer: EditorPointer<CompRef>) => TpaStyle
    >
  | IDependencyToken<
      DependencyToken.OOI_STYLE_GET,
      (editorPointer: EditorPointer<CompRef>) => TpaStyle
    >
  | IDependencyToken<
      DependencyToken.OOI_STYLE_UPDATE,
      (
        editorPointer: EditorPointer<CompRef>,
        values: TpaStyleParamValue[],
      ) => void
    >
  // Stage
  | IDependencyToken<
      DependencyToken.STAGE_BREAKPOINT_GET,
      (editorPointer: EditorPointer<CompRef>) => Breakpoint | undefined
    >
  | IDependencyToken<
      DependencyToken.STAGE_BREAKPOINT_POINTER_GET,
      (
        editorPointer: EditorPointer<CompRef>,
        breakpoint: ValueOf<typeof LAYOUT.BREAKPOINT>,
      ) => EditorPointer<CompRef>
    >
  // Props
  | IDependencyToken<
      DependencyToken.PROPS_REMOVE,
      (path: string[], propPath: string[]) => void
    >
  | IDependencyToken<
      DependencyToken.PROPS_UPDATE,
      (path: string[], props: Record<string, unknown>) => void
    >
  | IDependencyToken<
      DependencyToken.PROPS_GET,
      (path: string[]) => Record<string, unknown> | undefined
    >
  | IDependencyToken<
      DependencyToken.PROPS_OVERRIDES_UPDATE,
      (
        editorPointer: EditorPointer<CompRef>,
        propsToUpdate: Record<string, unknown>,
      ) => void
    >
  | IDependencyToken<
      DependencyToken.PROPS_OVERRIDES_GET,
      (
        editorPointer: EditorPointer<CompRef>,
      ) => Record<string, unknown> | undefined
    >
  | IDependencyToken<
      DependencyToken.PROPS_OVERRIDES_REMOVE,
      (editorPointer: EditorPointer<CompRef>, keys?: string[]) => void
    >
  | IDependencyToken<DependencyToken.DM_CHANGES_WAIT, () => Promise<void>>
  // Layout
  | IDependencyToken<
      DependencyToken.LAYOUT_COMPONENT_UPDATE,
      (params: {
        editorPointer: EditorPointer<CompRef>;
        targetSize: ValueOf<Presets>['initialSize'];
        parentPointer?: EditorPointer<CompRef>;
      }) => void
    >
  | IDependencyToken<
      DependencyToken.LAYOUT_CONTAINER_REGENERATE,
      (params: {
        editorPointer: EditorPointer<CompRef>;
        containerLayoutTransitionConfig: ContainerLayoutTransitionConfig;
      }) => void
    >
  // State
  | IDependencyToken<
      DependencyToken.VIEW_STATE_GET,
      (componentRef: CompRef) => string | null
    >
  | IDependencyToken<
      DependencyToken.VIEW_STATE_UPDATE,
      (componentRef: CompRef, state: string) => void
    >
  | IDependencyToken<
      DependencyToken.DM_STAGE_DATA_GET,
      (componentRef: CompRef) => any
    >
  // Design States
  | IDependencyToken<
      DependencyToken.DESIGN_STATES_GET,
      (editorPointers: EditorPointer<CompRef>[]) => string[]
    >
  | IDependencyToken<
      DependencyToken.DESIGN_STATES_SET,
      (editorPointers: EditorPointer<CompRef>[], states: string[]) => void
    >
  | IDependencyToken<
      DependencyToken.DESIGN_STATES_RESET,
      (editorPointers: EditorPointer<CompRef>[]) => void
    >
  // Presets
  | IDependencyToken<
      DependencyToken.BUILDER_PRESETS_GET_EFFECTIVE,
      (
        editorPointer: EditorPointer<CompRef>,
      ) => { presetName?: string } | undefined
    >
  | IDependencyToken<
      DependencyToken.BUILDER_PRESETS_GET,
      (
        editorPointer: EditorPointer<CompRef>,
      ) => { presetName?: string } | undefined
    >
  | IDependencyToken<
      DependencyToken.BUILDER_PRESETS_UPDATE,
      (editorPointer: EditorPointer<CompRef>, presetName: string) => void
    >
  // Resolved Data
  | IDependencyToken<DependencyToken.DM_SVG_INFO_GET, (uri: string) => any>
  // Client spec methods
  | IDependencyToken<
      DependencyToken.COMP_TYPE_GET,
      (editorPointer: EditorPointer<CompRef>) => string | null
    >
  | IDependencyToken<
      DependencyToken.LINK_DISPLAY_NAME_RESOLVE,
      (link: Link) => string
    >
  // Components
  | IDependencyToken<
      DependencyToken.COMPONENTS_CONTAINER_GET,
      (documentPointer: CompRef) => CompRef | null
    >
  | IDependencyToken<
      DependencyToken.COMPONENTS_CHILDREN_GET,
      (documentPointer: CompRef) => CompRef[]
    >
  | IDependencyToken<
      DependencyToken.COMPONENTS_SLOTS_GET_NAMES,
      (documentPointer: CompRef) => string[]
    >
  | IDependencyToken<
      DependencyToken.COMPONENTS_SLOTS_GET_DATA,
      (documentPointer: CompRef) => Record<string, CompRef | undefined>
    >
  | IDependencyToken<
      DependencyToken.COMPONENTS_SLOTS_BUILDER_GET_DATA,
      (documentPointer: CompRef) => PopulatedComponentContainerInfoMap
    >
  // Providers
  | IDependencyToken<
      DependencyToken.PROVIDERS_LIST,
      ProvidersServiceAPI['list']
    >
  | IDependencyToken<
      DependencyToken.PROVIDERS_ATTACH,
      ProvidersServiceAPI['attach']
    >
  | IDependencyToken<
      DependencyToken.PROVIDERS_DETACH,
      ProvidersServiceAPI['detach']
    >
  | IDependencyToken<
      DependencyToken.PROVIDERS_GET_DATA,
      ProvidersServiceAPI['getData']
    >
  | IDependencyToken<
      DependencyToken.PROVIDERS_UPDATE_DATA,
      ProvidersServiceAPI['updateData']
    >
  // Bindings
  | IDependencyToken<
      DependencyToken.BINDINGS_BIND,
      DataBindingServiceAPI['bind']
    >
  | IDependencyToken<
      DependencyToken.BINDINGS_SET_BINDING,
      DataBindingServiceAPI['setBinding']
    >
  | IDependencyToken<
      DependencyToken.BINDINGS_UNBIND,
      DataBindingServiceAPI['unbind']
    >
  | IDependencyToken<
      DependencyToken.BINDINGS_LIST,
      DataBindingServiceAPI['listBindings']
    >
  | IDependencyToken<
      DependencyToken.BINDINGS_GET_BINDING,
      DataBindingServiceAPI['getBinding']
    >
  | IDependencyToken<
      DependencyToken.BINDINGS_FIND_BINDING_CANDIDATES,
      DataBindingServiceAPI['findBindingCandidates']
    >
  // Panorama
  | IDependencyToken<
      DependencyToken.PANORAMA_TRACK_TRANSACTION,
      <A extends unknown[]>(
        func: (...args: A) => void,
        methodName: string,
        args: [...A],
        serializers?: {
          [K in keyof A]?: (arg: A[K]) => Record<string, unknown> | undefined;
        },
        extraContext?: Record<string, string>,
      ) => void
    >
  | IDependencyToken<
      DependencyToken.UX_DATA_GET_WITH_DEFAULTS,
      (params: {
        componentType: string;
        language: string;
      }) => Promise<Record<string, string>>
    >;
